import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { ChevronDown, ChevronRight, Trash2 } from "lucide-react";
import {
  adminSignalAnalytics,
  adminSetSignalResult,
  adminSignalsHistory,
  type AdminSignalRow,
  type SignalResult,
} from "@/lib/admin-signals.functions";
import { adminDeleteSignal } from "@/lib/admin.functions";
import { toast } from "sonner";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

export const Route = createFileRoute("/_authenticated/admin/signals")({
  component: SignalsPage,
});

const RESULT_OPTIONS: SignalResult[] = ["pending", "win", "loss", "breakeven", "cancelled"];

function resultBadge(r: SignalResult) {
  const map: Record<SignalResult, string> = {
    pending: "border-yellow-500/50 text-yellow-500",
    win: "border-emerald-500/50 text-emerald-500",
    loss: "border-red-500/50 text-red-500",
    breakeven: "border-zinc-500/50 text-zinc-400",
    cancelled: "border-zinc-700 text-zinc-500",
  };
  return (
    <Badge variant="outline" className={map[r]}>
      {r}
    </Badge>
  );
}

function actionBadge(a: string) {
  const cls =
    a === "BUY"
      ? "border-emerald-500/50 text-emerald-500"
      : a === "SELL"
        ? "border-red-500/50 text-red-500"
        : "border-yellow-500/50 text-yellow-500";
  return (
    <Badge variant="outline" className={cls}>
      {a}
    </Badge>
  );
}

function SignalsPage() {
  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold">Signal Intelligence</h1>
        <p className="text-sm text-muted-foreground">
          Audit signal history, mark outcomes, and review agent performance.
        </p>
      </div>
      <Tabs defaultValue="history" className="space-y-4">
        <TabsList>
          <TabsTrigger value="history">History</TabsTrigger>
          <TabsTrigger value="performance">Performance</TabsTrigger>
          <TabsTrigger value="agents">Agents</TabsTrigger>
        </TabsList>
        <TabsContent value="history">
          <HistoryTab />
        </TabsContent>
        <TabsContent value="performance">
          <PerformanceTab />
        </TabsContent>
        <TabsContent value="agents">
          <AgentsTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function HistoryTab() {
  const qc = useQueryClient();
  const history = useServerFn(adminSignalsHistory);
  const setResult = useServerFn(adminSetSignalResult);
  const del = useServerFn(adminDeleteSignal);

  const [pair, setPair] = useState("");
  const [action, setAction] = useState<string>("all");
  const [result, setResult2] = useState<string>("all");
  const [minScore, setMinScore] = useState<string>("");
  const [expanded, setExpanded] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const pageSize = 25;

  const filterPayload = useMemo(
    () => ({
      pair: pair.trim() || undefined,
      action: action !== "all" ? (action as "BUY" | "SELL" | "WAIT") : undefined,
      result: result !== "all" ? (result as SignalResult) : undefined,
      minScore: minScore ? Number(minScore) : undefined,
      limit: 200,
    }),
    [pair, action, result, minScore],
  );

  const { data, isLoading } = useQuery({
    queryKey: ["admin", "signals-history", filterPayload],
    queryFn: () => history({ data: filterPayload }),
  });

  const setResultMut = useMutation({
    mutationFn: (v: { id: string; result: SignalResult }) =>
      setResult({ data: { id: v.id, result: v.result } }),
    onSuccess: () => {
      toast.success("Result updated");
      qc.invalidateQueries({ queryKey: ["admin", "signals-history"] });
      qc.invalidateQueries({ queryKey: ["admin", "signal-analytics"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const delMut = useMutation({
    mutationFn: (id: string) => del({ data: { id } }),
    onSuccess: () => {
      toast.success("Signal deleted");
      qc.invalidateQueries({ queryKey: ["admin", "signals-history"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const rows = data?.signals ?? [];
  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
  const pageRows = rows.slice((page - 1) * pageSize, page * pageSize);

  return (
    <Card className="border-border/60 bg-card/60">
      <div className="flex flex-wrap items-end gap-3 border-b border-border/60 p-4">
        <div className="space-y-1">
          <label className="text-[11px] uppercase tracking-widest text-muted-foreground">Pair</label>
          <Input
            value={pair}
            onChange={(e) => {
              setPair(e.target.value.toUpperCase());
              setPage(1);
            }}
            placeholder="EURUSD"
            className="h-8 w-32"
          />
        </div>
        <div className="space-y-1">
          <label className="text-[11px] uppercase tracking-widest text-muted-foreground">Action</label>
          <Select
            value={action}
            onValueChange={(v) => {
              setAction(v);
              setPage(1);
            }}
          >
            <SelectTrigger className="h-8 w-28">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="BUY">BUY</SelectItem>
              <SelectItem value="SELL">SELL</SelectItem>
              <SelectItem value="WAIT">WAIT</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <label className="text-[11px] uppercase tracking-widest text-muted-foreground">Result</label>
          <Select
            value={result}
            onValueChange={(v) => {
              setResult2(v);
              setPage(1);
            }}
          >
            <SelectTrigger className="h-8 w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              {RESULT_OPTIONS.map((r) => (
                <SelectItem key={r} value={r}>
                  {r}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <label className="text-[11px] uppercase tracking-widest text-muted-foreground">
            Min score
          </label>
          <Input
            type="number"
            value={minScore}
            onChange={(e) => {
              setMinScore(e.target.value);
              setPage(1);
            }}
            placeholder="0"
            className="h-8 w-24"
          />
        </div>
        <div className="ml-auto text-xs text-muted-foreground">
          {isLoading ? "Loading…" : `${rows.length} results`}
        </div>
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-8" />
            <TableHead>Time</TableHead>
            <TableHead>Pair</TableHead>
            <TableHead>Action</TableHead>
            <TableHead>Score</TableHead>
            <TableHead>Entry</TableHead>
            <TableHead>SL / TP</TableHead>
            <TableHead>User</TableHead>
            <TableHead>Result</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {pageRows.map((s) => (
            <SignalRow
              key={s.id}
              row={s}
              expanded={expanded === s.id}
              onToggle={() => setExpanded(expanded === s.id ? null : s.id)}
              onSetResult={(r) => setResultMut.mutate({ id: s.id, result: r })}
              onDelete={() => delMut.mutate(s.id)}
            />
          ))}
          {!isLoading && pageRows.length === 0 && (
            <TableRow>
              <TableCell colSpan={10} className="p-6 text-center text-muted-foreground">
                No signals match the filters.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      <div className="flex items-center justify-between border-t border-border/60 p-3 text-xs">
        <span className="text-muted-foreground">
          Page {page} / {totalPages}
        </span>
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="outline"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            Prev
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          >
            Next
          </Button>
        </div>
      </div>
    </Card>
  );
}

function SignalRow({
  row,
  expanded,
  onToggle,
  onSetResult,
  onDelete,
}: {
  row: AdminSignalRow;
  expanded: boolean;
  onToggle: () => void;
  onSetResult: (r: SignalResult) => void;
  onDelete: () => void;
}) {
  let parsedPayload: unknown = null;
  if (row.payload) {
    try {
      parsedPayload = JSON.parse(row.payload);
    } catch {
      parsedPayload = row.payload;
    }
  }
  const agents = Array.isArray((parsedPayload as { agents?: unknown[] })?.agents)
    ? ((parsedPayload as { agents: unknown[] }).agents as Array<Record<string, unknown>>)
    : Array.isArray((parsedPayload as { breakdown?: unknown[] })?.breakdown)
      ? ((parsedPayload as { breakdown: unknown[] }).breakdown as Array<Record<string, unknown>>)
      : [];

  return (
    <>
      <TableRow>
        <TableCell>
          <Button size="icon" variant="ghost" className="h-6 w-6" onClick={onToggle}>
            {expanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
          </Button>
        </TableCell>
        <TableCell className="text-xs text-muted-foreground">
          {new Date(row.created_at).toLocaleString()}
        </TableCell>
        <TableCell className="font-mono">{row.pair}</TableCell>
        <TableCell>{actionBadge(row.action)}</TableCell>
        <TableCell className="tabular-nums">{row.score.toFixed(0)}</TableCell>
        <TableCell className="font-mono text-xs">
          {row.entry !== null ? row.entry.toFixed(5) : "—"}
        </TableCell>
        <TableCell className="font-mono text-xs text-muted-foreground">
          {row.stop_loss !== null ? row.stop_loss.toFixed(5) : "—"} /{" "}
          {row.take_profit !== null ? row.take_profit.toFixed(5) : "—"}
        </TableCell>
        <TableCell className="max-w-[180px] truncate text-xs text-muted-foreground">
          {row.user_email ?? row.user_id.slice(0, 8)}
        </TableCell>
        <TableCell>
          <Select value={row.result} onValueChange={(v) => onSetResult(v as SignalResult)}>
            <SelectTrigger className="h-7 w-28">
              <SelectValue>{resultBadge(row.result)}</SelectValue>
            </SelectTrigger>
            <SelectContent>
              {RESULT_OPTIONS.map((r) => (
                <SelectItem key={r} value={r}>
                  {r}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </TableCell>
        <TableCell className="text-right">
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-600">
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete signal?</AlertDialogTitle>
                <AlertDialogDescription>
                  This permanently removes the signal record. This cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={onDelete}>Delete</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </TableCell>
      </TableRow>
      {expanded && (
        <TableRow>
          <TableCell colSpan={10} className="bg-muted/20">
            <div className="space-y-3 p-3">
              {agents.length > 0 ? (
                <div>
                  <div className="mb-2 text-[11px] uppercase tracking-widest text-muted-foreground">
                    Council breakdown
                  </div>
                  <div className="grid grid-cols-2 gap-2 md:grid-cols-3 lg:grid-cols-4">
                    {agents.map((a, i) => (
                      <div key={i} className="rounded border border-border/60 bg-card/40 p-2 text-xs">
                        <div className="font-medium">{String(a.name ?? a.agent ?? `Agent ${i + 1}`)}</div>
                        <div className="text-muted-foreground">
                          {a.direction ? String(a.direction) : "—"} · weight{" "}
                          {Number(a.weight ?? a.score ?? 0).toFixed(2)}
                        </div>
                        {a.reasoning ? (
                          <div className="mt-1 text-[11px] text-muted-foreground line-clamp-3">
                            {String(a.reasoning)}
                          </div>
                        ) : null}
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-xs text-muted-foreground">No council breakdown in payload.</div>
              )}
              {row.result_notes && (
                <div className="text-xs">
                  <span className="text-muted-foreground">Notes: </span>
                  {row.result_notes}
                </div>
              )}
              <details className="text-xs">
                <summary className="cursor-pointer text-muted-foreground">Raw payload</summary>
                <pre className="mt-2 overflow-auto rounded bg-muted/30 p-2 text-[11px]">
                  {JSON.stringify(parsedPayload, null, 2)}
                </pre>
              </details>
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

function PerformanceTab() {
  const fn = useServerFn(adminSignalAnalytics);
  const { data, isLoading } = useQuery({
    queryKey: ["admin", "signal-analytics"],
    queryFn: () => fn(),
  });
  const a = data?.analytics;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Metric label="Total (30d)" value={a?.total ?? 0} loading={isLoading} />
        <Metric label="Resolved" value={a?.resolved ?? 0} loading={isLoading} />
        <Metric
          label="Win rate"
          value={a ? `${a.winRate.toFixed(1)}%` : "—"}
          loading={isLoading}
          accent={a && a.winRate >= 50 ? "good" : "bad"}
        />
        <Metric
          label="Avg score"
          value={a ? a.avgScore.toFixed(1) : "—"}
          loading={isLoading}
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card className="border-border/60 bg-card/60 p-4">
          <h3 className="text-sm font-semibold">Daily volume (30d)</h3>
          <div className="mt-3 h-56">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={a?.recentDaily ?? []}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.15} />
                <XAxis dataKey="date" fontSize={10} />
                <YAxis fontSize={10} />
                <Tooltip
                  contentStyle={{
                    background: "hsl(var(--card))",
                    border: "1px solid hsl(var(--border))",
                    fontSize: "12px",
                  }}
                />
                <Area type="monotone" dataKey="total" stroke="#60a5fa" fill="#60a5fa33" />
                <Area type="monotone" dataKey="wins" stroke="#10b981" fill="#10b98133" />
                <Area type="monotone" dataKey="losses" stroke="#ef4444" fill="#ef444433" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>
        <Card className="border-border/60 bg-card/60 p-4">
          <h3 className="text-sm font-semibold">Win rate by pair</h3>
          <div className="mt-3 h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={a?.byPair ?? []}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.15} />
                <XAxis dataKey="pair" fontSize={10} />
                <YAxis fontSize={10} domain={[0, 100]} />
                <Tooltip
                  contentStyle={{
                    background: "hsl(var(--card))",
                    border: "1px solid hsl(var(--border))",
                    fontSize: "12px",
                  }}
                />
                <Bar dataKey="winRate" fill="#10b981" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      <Card className="border-border/60 bg-card/60">
        <div className="border-b border-border/60 p-4 text-sm font-semibold">By pair</div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Pair</TableHead>
              <TableHead className="text-right">Total</TableHead>
              <TableHead className="text-right">Wins</TableHead>
              <TableHead className="text-right">Losses</TableHead>
              <TableHead className="text-right">Win rate</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(a?.byPair ?? []).map((p) => (
              <TableRow key={p.pair}>
                <TableCell className="font-mono">{p.pair}</TableCell>
                <TableCell className="text-right tabular-nums">{p.total}</TableCell>
                <TableCell className="text-right tabular-nums text-emerald-500">{p.wins}</TableCell>
                <TableCell className="text-right tabular-nums text-red-500">{p.losses}</TableCell>
                <TableCell className="text-right tabular-nums">{p.winRate.toFixed(1)}%</TableCell>
              </TableRow>
            ))}
            {!isLoading && (!a || a.byPair.length === 0) && (
              <TableRow>
                <TableCell colSpan={5} className="p-6 text-center text-muted-foreground">
                  No data yet.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}

function AgentsTab() {
  const fn = useServerFn(adminSignalAnalytics);
  const { data, isLoading } = useQuery({
    queryKey: ["admin", "signal-analytics"],
    queryFn: () => fn(),
  });
  const agents = data?.analytics.agents ?? [];

  if (isLoading) {
    return <p className="text-sm text-muted-foreground">Loading agent stats…</p>;
  }
  if (agents.length === 0) {
    return (
      <Card className="border-border/60 bg-card/60 p-6 text-sm text-muted-foreground">
        No agent breakdown found in signal payloads yet. Once signals include a council `agents` array,
        per-agent performance will appear here.
      </Card>
    );
  }
  return (
    <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
      {agents.map((ag) => (
        <Card key={ag.agent} className="border-border/60 bg-card/60 p-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold">{ag.agent}</h3>
            <Badge
              variant="outline"
              className={
                ag.winRate >= 55
                  ? "border-emerald-500/50 text-emerald-500"
                  : ag.winRate >= 45
                    ? "border-yellow-500/50 text-yellow-500"
                    : "border-red-500/50 text-red-500"
              }
            >
              {ag.winRate.toFixed(0)}% win
            </Badge>
          </div>
          <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-muted-foreground">
            <div>
              <div className="text-[10px] uppercase tracking-widest">Appearances</div>
              <div className="text-base text-foreground tabular-nums">{ag.appearances}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest">Avg weight</div>
              <div className="text-base text-foreground tabular-nums">{ag.avgWeight.toFixed(2)}</div>
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
}

function Metric({
  label,
  value,
  loading,
  accent,
}: {
  label: string;
  value: string | number;
  loading?: boolean;
  accent?: "good" | "bad";
}) {
  const color =
    accent === "good" ? "text-emerald-500" : accent === "bad" ? "text-red-500" : "text-foreground";
  return (
    <Card className="border-border/60 bg-card/60 p-4">
      <div className="text-[11px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className={`mt-1 text-2xl font-semibold tabular-nums ${color}`}>
        {loading ? "…" : value}
      </div>
    </Card>
  );
}
